import Store from '@ember-data/store';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { isNothing } from 'donor-elf-web/lib/object-utils';
import type {
  BulkActionCustomRenameCustomProperty,
  BulkActionResponseIncludesUpdatedFundProperties,
  BulkActionType,
} from 'donor-elf-web/models/bulk-action';
import ContactsCache, {
  type ContactCustomPropertyInfo,
} from 'donor-elf-web/services/contacts-cache';
import Settings from 'donor-elf-web/services/settings';
import Changeset from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';

interface ComponentArgs {
  propertyInfo: ContactCustomPropertyInfo;
  onClose: () => void;
}

let validateNewLabel = (
  currentCustomProperties: ContactCustomPropertyInfo[],
) => {
  return {
    newLabel: (_key: string, newValue: string): string | true => {
      if (isNothing(newValue)) {
        return 'Must be present';
      }
      let lowercaseNewLabel = newValue.toLowerCase().trim();
      if (
        currentCustomProperties.some(
          (p) => p.l.toLowerCase() === lowercaseNewLabel,
        )
      ) {
        return 'That property name already exists';
      }
      return true;
    },
  };
};

export default class ContactDetailsPropertiesCustomPropertyRenameModalComponent extends Component<ComponentArgs> {
  constructor(owner: any, args: ComponentArgs) {
    super(owner, args);
    let validation = validateNewLabel(this.contactsCache.customProperties);
    this.changeset = new Changeset(
      { newLabel: '' },
      lookupValidator(validation),
      validation,
    );
  }

  @service declare contactsCache: ContactsCache;
  @service declare settings: Settings;
  @service declare store: Store;

  @tracked changeset?: any;
  @tracked showValidationErrors = false;

  bulkActionType: BulkActionType = 'rename_custom_property';
  @tracked bulkActionCustom?: BulkActionCustomRenameCustomProperty;

  @tracked renaming = false;
  @tracked renameError = false;

  @action
  async rename() {
    await this.changeset.validate();
    if (this.changeset.get('isValid')) {
      if (this.showValidationErrors) {
        this.showValidationErrors = false;
      }
      this.bulkActionCustom = {
        property_label: this.args.propertyInfo.l,
        new_label: this.changeset.get('newLabel'),
      };
      this.renaming = true;
    } else {
      this.showValidationErrors = true;
    }
  }

  @action
  async onCompleted(response: BulkActionResponseIncludesUpdatedFundProperties) {
    await this.contactsCache.reloadSpecificContacts(response.contact_ids);
    await this.settings.contactCustomPropertyUserSetting?.reload();
    this.settings.fundSettings!.contactCustomInfo!.properties =
      response.updated_fund_properties;
    // break the custom properties cache
    this.contactsCache.customPropertiesCacheIndex += 1;
  }

  @action
  async onError() {
    this.renameError = true;
  }
}
